import { CloseIcon } from "@chakra-ui/icons"
import {
  Box,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Text as ChakraText,
  VStack,
} from "@chakra-ui/react"
import { cloneDeep } from "lodash"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { colors, IKReactSelect } from "../../../../ui-kit"
import { useEffectSkipInitial } from "../../../hooks/useEffectSkipInitial"

enum OptionType {
  String = "String",
  Number = "Number",
  Boolean = "Boolean",
}

const getInitialValue = (optionType: OptionType): boolean | string => {
  if (optionType === OptionType.Boolean) {
    return true
  }

  return ""
}

const OptionTypeSelectMenu = ({
  fieldsMetadata,
  index,
  setFieldsMetadata,
}): React.ReactElement => {
  const metadata = useMemo(
    () => fieldsMetadata[index],
    [fieldsMetadata, index],
  )

  return (
    <IKReactSelect 
      menuPlacement="auto"
      variant="compact"
      captureMenuScroll={false}
      value={{
        label: metadata.optionType,
        value: metadata.optionType,
      }}
      onChange={newValue => {
        const newFieldsMetadata = [...fieldsMetadata]
        newFieldsMetadata[index].optionType = newValue.value
        newFieldsMetadata[index].value = getInitialValue(metadata.optionType)
        setFieldsMetadata(newFieldsMetadata)
      }}
      options={[
        {
          label: OptionType.String,
          value: OptionType.String,
        },
        {
          label: OptionType.Number,
          value: OptionType.Number,
        },
        {
          label: OptionType.Boolean,
          value: OptionType.Boolean,
        },
      ]}
    />
  )
}

const OptionValueField = ({
  fieldsMetadata,
  index,
  setFieldsMetadata,
}): React.ReactElement => {
  const handleValueChange = useCallback((newValue) => {
    const newFieldsMetadata = [...fieldsMetadata]
    newFieldsMetadata[index].value = newValue
    setFieldsMetadata(newFieldsMetadata)
  }, [fieldsMetadata, index, setFieldsMetadata])

  const metadata = useMemo(
    () => fieldsMetadata[index],
    [fieldsMetadata, index],
  )

  const valueField = useMemo(() => {
    if (metadata.optionType === OptionType.String) {
      return (
        <Input
          value={metadata.value}
          onChange={e => handleValueChange(e.target.value)}
        />
      )
    }

    if (metadata.optionType === OptionType.Number) {
      return (
        <Input
          value={metadata.value}
          type="number"
          onChange={e => {handleValueChange(
            e.target.value !== "" ?
            Number(e.target.value) :
            ""
          )}}
        />
      )
    }

    if (metadata.optionType === OptionType.Boolean) {
      return (
        <IKReactSelect
          menuPlacement="auto"
          variant="compact"
          value={{
            label: String(metadata.value),
            value: metadata.value,
          }}
          options={[
            {
              label: "true",
              value: true,
            },
            {
              label: "false",
              value: false
            },
          ]}
          onChange={newValue => {
            handleValueChange(newValue.value)
          }}
        />
      )
    }
  }, [fieldsMetadata, index, metadata, handleValueChange])

  return (
    <HStack width="100%">
      <Box flex="2 0">
        <OptionTypeSelectMenu {...{fieldsMetadata, index, setFieldsMetadata}} />
      </Box>

      <Box flex="4 0">
        <FormControl isInvalid={metadata.error !== ""}>
          {valueField}
          <FormErrorMessage>{metadata.error}</FormErrorMessage>
        </FormControl>
      </Box>
    </HStack>
  )
}

const SelectOptionsField = ({
  field,
  meta,
  form,
}): React.ReactElement => {
  const { t } = useTranslation()

  const [fieldsMetadata, setFieldsMetadata] = useState<Array<{
    optionType: OptionType
    value: string | boolean | number
    error: string
  }>>(
    field?.value?.map(value => {
      let optionType
      if (typeof value === "number") {
        optionType = OptionType.Number
      }

      if (typeof value === "boolean") {
        optionType = OptionType.Boolean
      }

      if (typeof value === "string") {
        optionType = OptionType.String
      }

      return {
        optionType,
        value,
        error: "",
      }
    }) ?? []
  )

  useEffectSkipInitial(() => {
    setFieldsMetadata([])
  }, [form.values.schema.type])

  useEffect(() => {
    const newSelectOptions = fieldsMetadata.filter(metadata => {
      if (metadata.value === "" || typeof metadata.value === "undefined") {
        return false
      }
      return true
    }).map(metadata => metadata.value)

    // if default value isn't one of the select options anymore, then unset it
    const unsetDefaultValue =
      Array.isArray(form.values?.schema?.defaultValue) ?
      !(form.values.schema.defaultValue.every(value => newSelectOptions.includes(value))) :
      !newSelectOptions.includes(form.values?.schema?.defaultValue)

    const newFormValues = cloneDeep(form.values)

    newFormValues.schema.selectOptions = newSelectOptions
    if (unsetDefaultValue) {
      delete newFormValues.schema.defaultValue
    }

    form.setValues(newFormValues)
  }, [fieldsMetadata])

  return (
    <VStack width="full" alignItems="flex-start">
      {
        fieldsMetadata.map((metadata, index) => (
          <HStack width="full">
            <OptionValueField {...{fieldsMetadata, index, setFieldsMetadata}} />
            <CloseIcon
              cursor="pointer"
              boxSize="0.825rem"
              color={colors.appBattleshipGrey}
              onClick={() => {
                const newFieldsMetadata = [...fieldsMetadata]
                newFieldsMetadata.splice(index, 1)
                setFieldsMetadata(newFieldsMetadata)
              }}
            />
          </HStack>
        ))
      }

      <ChakraText
        color={colors.appClearBlue}
        fontWeight="medium"
        cursor="pointer"
        onClick={() => {
          setFieldsMetadata([
            ...fieldsMetadata,
            {
              error: "",
              optionType: OptionType.String,
              value: "",
            }
          ])
        }}
      >
        {t("plus_create_an_option", "+ Create an option")}
      </ChakraText>
    </VStack>
  )
}

export default SelectOptionsField
